import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { movieService } from '@watchlist/shared';

/**
 * MovieRatingContext - Batch rating fetcher with caching
 * 
 * This context provides an efficient way to fetch and cache movie ratings
 * for list views. Instead of each StandardMovieCard making an individual
 * API call, list components can prefetch ratings in batches.
 * 
 * Benefits:
 * - Reduces N individual calls to ceil(N/10) batch calls (~90% reduction)
 * - Caches ratings to prevent redundant fetches
 * - Debounces prefetch requests to batch them efficiently
 */

interface MovieRatingContextType {
    /**
     * Get the cached average rating for a movie
     * Returns null if not cached or if movie has < 2 ratings
     */
    getRating: (movieId: string) => number | null;

    /**
     * Check if a rating is currently being fetched
     */
    isLoading: (movieId: string) => boolean;

    /**
     * Prefetch ratings for multiple movies
     * Automatically handles batching and caching
     */
    prefetchRatings: (movieIds: string[]) => void;

    /**
     * Clear the cache (useful for refreshing data)
     */
    clearCache: () => void;
}

const MovieRatingContext = createContext<MovieRatingContextType | null>(null);

interface MovieRatingProviderProps {
    children: React.ReactNode;
}

export const MovieRatingProvider: React.FC<MovieRatingProviderProps> = ({ children }) => {
    // Cache: movieId -> average rating (or -1 if fetched but no rating available)
    const [ratingsCache, setRatingsCache] = useState<Map<string, number>>(new Map());
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

    // Track pending prefetch requests for debouncing
    const pendingIdsRef = useRef<Set<string>>(new Set());
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const isFetchingRef = useRef(false);

    const getRating = useCallback((movieId: string): number | null => {
        const cached = ratingsCache.get(movieId);
        // Return null if not cached or if marked as "no rating" (-1)
        if (cached === undefined || cached === -1) {
            return null;
        }
        return cached;
    }, [ratingsCache]);

    const isLoading = useCallback((movieId: string): boolean => {
        return loadingIds.has(movieId);
    }, [loadingIds]);

    const clearCache = useCallback(() => {
        setRatingsCache(new Map());
        setLoadingIds(new Set());
        pendingIdsRef.current.clear();
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    const executeFetch = useCallback(async () => {
        if (isFetchingRef.current || pendingIdsRef.current.size === 0) {
            return;
        }

        isFetchingRef.current = true;

        // Get uncached IDs from pending
        const uncachedIds = Array.from(pendingIdsRef.current).filter(
            id => !ratingsCache.has(id) && id && id !== 'unknown'
        );

        // Clear pending set
        pendingIdsRef.current.clear();

        if (uncachedIds.length === 0) {
            isFetchingRef.current = false;
            return;
        }

        // Mark as loading
        setLoadingIds(prev => {
            const next = new Set(prev);
            uncachedIds.forEach(id => next.add(id));
            return next;
        });

        try {
            console.log(`MovieRatingContext: Fetching ratings for ${uncachedIds.length} movies`);
            const newRatings = await movieService.getMovieRatingsBatch(uncachedIds);

            // Update cache with new ratings
            setRatingsCache(prev => {
                const next = new Map(prev);

                // Add ratings that were found
                newRatings.forEach((rating, movieId) => {
                    next.set(movieId, rating);
                });

                // Mark movies with no ratings as -1 (fetched but no rating)
                uncachedIds.forEach(id => {
                    if (!newRatings.has(id)) {
                        next.set(id, -1);
                    }
                });

                return next;
            });

            console.log(`MovieRatingContext: Cached ${newRatings.size} ratings, ${uncachedIds.length - newRatings.size} movies had no ratings`);
        } catch (error) {
            console.error('MovieRatingContext: Failed to fetch ratings batch:', error);
            // Mark all as fetched (no ratings) to prevent infinite retries
            setRatingsCache(prev => {
                const next = new Map(prev);
                uncachedIds.forEach(id => next.set(id, -1));
                return next;
            });
        } finally {
            // Clear loading state
            setLoadingIds(prev => {
                const next = new Set(prev);
                uncachedIds.forEach(id => next.delete(id));
                return next;
            });

            isFetchingRef.current = false;

            // If more IDs were added while fetching, process them
            if (pendingIdsRef.current.size > 0) {
                debounceTimerRef.current = setTimeout(executeFetch, 100);
            }
        }
    }, [ratingsCache]);

    const prefetchRatings = useCallback((movieIds: string[]) => {
        // Filter out already cached and invalid IDs
        const uncachedIds = movieIds.filter(
            id => id && id !== 'unknown' && !ratingsCache.has(id) && !pendingIdsRef.current.has(id)
        );

        if (uncachedIds.length === 0) {
            return;
        }

        // Add to pending set
        uncachedIds.forEach(id => pendingIdsRef.current.add(id));

        // Clear existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Debounce the fetch to batch rapid prefetch calls
        debounceTimerRef.current = setTimeout(executeFetch, 150);
    }, [ratingsCache, executeFetch]);

    const contextValue = useMemo(() => ({
        getRating,
        isLoading,
        prefetchRatings,
        clearCache
    }), [getRating, isLoading, prefetchRatings, clearCache]);

    return (
        <MovieRatingContext.Provider value={contextValue}>
            {children}
        </MovieRatingContext.Provider>
    );
};

/**
 * Hook to access movie ratings from the context
 * Must be used within a MovieRatingProvider
 */
export const useMovieRatings = (): MovieRatingContextType => {
    const context = useContext(MovieRatingContext);
    if (!context) {
        throw new Error('useMovieRatings must be used within a MovieRatingProvider');
    }
    return context;
};

/**
 * Optional hook that returns null instead of throwing if outside provider
 * Useful for components that may or may not be rendered within a provider
 */
export const useMovieRatingsOptional = (): MovieRatingContextType | null => {
    return useContext(MovieRatingContext);
};

export default MovieRatingProvider;
